import React from 'react';

interface GlobalData {
  sgx_nifty?: number;
  sgx_nifty_change?: number;
  sgx_nifty_change_pct?: number;
  dow_futures?: number;
  dow_futures_change_pct?: number;
  nasdaq_futures?: number;
  nasdaq_futures_change_pct?: number;
  gold?: number;
  gold_change_pct?: number;
  crude_oil?: number;
  crude_oil_change_pct?: number;
  dollar_index?: number;
  dollar_index_change?: number;
  usd_inr?: number;
  usd_inr_change?: number;
  nikkei?: number;
  nikkei_change_pct?: number;
  global_sentiment?: string;
  created_at?: string;
}

interface Props {
  data: GlobalData | null;
  loading?: boolean;
  refreshing?: boolean;
  onRefresh?: () => void;
}

interface MarketItem {
  label: string;
  value?: number;
  change?: number;
  invertBullish?: boolean;
  isPct?: boolean;  // false = change is an absolute value, not a percentage
}

export default function GlobalMarketsBar({ data, loading, refreshing = false, onRefresh }: Props) {
  if (loading || !data) {
    return (
      <div style={{
        background: '#0a0a10', border: '1px solid #1a1a1a', borderRadius: 6,
        padding: '8px 12px', display: 'flex', gap: 16, overflowX: 'auto',
        alignItems: 'center',
      }}>
        <span style={{ color: '#444', fontSize: '0.7rem' }}>Loading global markets...</span>
      </div>
    );
  }

  const items: MarketItem[] = [
    { label: 'SGX Nifty', value: data.sgx_nifty, change: data.sgx_nifty_change_pct, isPct: true },
    { label: 'Dow Futures', value: data.dow_futures, change: data.dow_futures_change_pct, isPct: true },
    { label: 'Nasdaq Fut', value: data.nasdaq_futures, change: data.nasdaq_futures_change_pct, isPct: true },
    { label: 'Gold', value: data.gold, change: data.gold_change_pct, isPct: true },
    { label: 'Crude Oil', value: data.crude_oil, change: data.crude_oil_change_pct, invertBullish: true, isPct: true },
    { label: 'DXY', value: data.dollar_index, change: data.dollar_index_change, invertBullish: true, isPct: false },
    { label: 'USD/INR', value: data.usd_inr, change: data.usd_inr_change, invertBullish: true, isPct: false },
    { label: 'Nikkei', value: data.nikkei, change: data.nikkei_change_pct, isPct: true },
  ];

  const sentColor: Record<string, string> = {
    bullish: '#26a65b',
    bearish: '#e74c3c',
    neutral: '#f39c12',
  };

  const formatValue = (v?: number): string => {
    if (v === undefined || v === null) return '—';
    if (v > 1000) return v.toLocaleString('en-IN', { maximumFractionDigits: 0 });
    return v.toFixed(2);
  };

  return (
    <div style={{
      background: '#0a0a10', border: '1px solid #1a1a1a', borderRadius: 6,
      padding: '6px 12px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, overflowX: 'auto' }}>
        {/* Overall sentiment */}
        {data.global_sentiment && (
          <div style={{ marginRight: 8, flexShrink: 0 }}>
            <div style={{ color: '#555', fontSize: '0.58rem' }}>GLOBAL</div>
            <div style={{
              color: sentColor[data.global_sentiment] || '#888',
              fontSize: '0.7rem', fontWeight: 700,
            }}>
              {data.global_sentiment.toUpperCase()}
            </div>
          </div>
        )}

        <div style={{ width: 1, height: 28, background: '#222', flexShrink: 0 }} />

        {items.map((item, idx) => {
          if (!item.value) return null;
          const chg = item.change ?? 0;
          const isBull = item.invertBullish ? chg < 0 : chg > 0;
          const color = chg === 0 ? '#888' : isBull ? '#26a65b' : '#e74c3c';
          const arrow = chg > 0 ? '▲' : chg < 0 ? '▼' : '—';

          return (
            <React.Fragment key={idx}>
              <div style={{ flexShrink: 0, minWidth: 70 }}>
                <div style={{ color: '#555', fontSize: '0.58rem' }}>{item.label}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
                  <span style={{ color: '#ccc', fontSize: '0.72rem' }}>{formatValue(item.value)}</span>
                  <span style={{ color, fontSize: '0.62rem' }}>
                    {arrow}{Math.abs(chg).toFixed(2)}{item.isPct !== false ? '%' : ''}
                  </span>
                </div>
              </div>
              {idx < items.length - 1 && (
                <div style={{ width: 1, height: 22, background: '#1a1a1a', flexShrink: 0 }} />
              )}
            </React.Fragment>
          );
        })}

        {data.created_at && (
          <>
            <div style={{ width: 1, height: 22, background: '#222', flexShrink: 0 }} />
            <span style={{ color: '#333', fontSize: '0.58rem', flexShrink: 0 }}>
              {new Date(data.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </>
        )}

        {onRefresh && (
          <>
            <div style={{ width: 1, height: 22, background: '#222', flexShrink: 0 }} />
            <button
              onClick={onRefresh}
              disabled={refreshing}
              title="Refresh global market data (Yahoo Finance)"
              style={{
                background: 'transparent', border: '1px solid #1a2a1a',
                color: refreshing ? '#555' : '#4caf50',
                fontSize: '0.58rem', padding: '2px 7px', borderRadius: 4,
                cursor: refreshing ? 'default' : 'pointer',
                flexShrink: 0, opacity: refreshing ? 0.5 : 1,
              }}
            >
              {refreshing ? '⟳' : '↺'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
